import { getAccessToken } from '../authentication/supabaseService';
import type { Recipe } from './recipesService';
import type { AllergenDetailsV1, AllergenDraft } from '../../types/RecipeAllergens';

export class RecipeApiError extends Error {
  constructor(public code: string, public status: number) { super(code); }
}

export async function throwRecipeApiError(response: Response): Promise<never> {
  const data = await response.json().catch(() => ({}));
  throw new RecipeApiError(response.status === 409 ? 'CONFLICT' : data.error ?? 'REQUEST_FAILED', response.status);
}

const ERROR_CODES = ['INVALID_FILE', 'UNSUPPORTED_VERSION', 'INVALID_HEADERS', 'TOO_MANY_ROWS', 'FORMULA_CELL',
  'INVALID_VALUE', 'INVALID_RECIPE_ID', 'DUPLICATE_TARGET', 'NO_CHANGES', 'NO_MATCH', 'CONFLICT', 'PLAN_EXPIRED',
  'INVALID_SELECTION', 'ACCESS_DENIED', 'INCOMPLETE_REVIEW', 'CONTRADICTORY_ALLERGENS',
  'REVIEWED_IMPORT_REQUIRED', 'TOO_MANY_PLANS', 'NOT_FOUND', 'INTERNAL_ERROR', 'UNMAPPED_LEGACY_ALLERGENS'];

export function allergenErrorKey(error: unknown): string {
  const code = typeof error === 'string' ? error : error instanceof RecipeApiError ? error.code : '';
  return `recipeAllergens.errors.${ERROR_CODES.includes(code) ? code : 'REQUEST_FAILED'}`;
}

function recipeFromJson(recipe: Recipe): Recipe {
  return { ...recipe, createdAt: new Date(recipe.createdAt), updatedAt: new Date(recipe.updatedAt) };
}

export async function saveRecipeAllergens(recipeId: string, expectedRevision: string, declaration: AllergenDraft): Promise<Recipe> {
  const token = await getAccessToken();
  const response = await fetch(`/api/recipes/${encodeURIComponent(recipeId)}/allergens`, {
    method: 'PATCH', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ expectedRevision, declaration }),
  });
  if (!response.ok) await throwRecipeApiError(response);
  return recipeFromJson(await response.json());
}

export interface AllergenImportCandidate {
  recipeId: string;
  name: string;
  current: AllergenDetailsV1;
  proposed: AllergenDetailsV1;
}

export interface AllergenImportRow {
  rowId: number;
  recipeName: string;
  recipeId?: string;
  matchType: 'id' | 'exact' | 'ambiguous' | 'suggestion' | 'none';
  candidates: AllergenImportCandidate[];
  errors: string[];
}

export interface AllergenImportPlan {
  planId: string;
  expiresAt: string;
  rows: AllergenImportRow[];
}

export interface AllergenImportSelection { rowId: number; recipeId: string; reviewed?: boolean }

export async function previewAllergenImport(restaurantId: string, file: File): Promise<AllergenImportPlan> {
  const token = await getAccessToken();
  const body = new FormData();
  body.append('file', file);
  const response = await fetch(`/api/recipes/restaurant/${encodeURIComponent(restaurantId)}/allergen-import/preview`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}` }, body,
  });
  if (!response.ok) await throwRecipeApiError(response);
  return response.json();
}

export async function commitAllergenImport(restaurantId: string, planId: string, idempotencyKey: string,
  selections: AllergenImportSelection[]): Promise<Recipe[]> {
  const token = await getAccessToken();
  const response = await fetch(`/api/recipes/restaurant/${encodeURIComponent(restaurantId)}/allergen-import/commit`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ planId, idempotencyKey, selections }),
  });
  if (!response.ok) await throwRecipeApiError(response);
  const data = await response.json();
  return data.recipes.map(recipeFromJson);
}

export async function downloadAllergenWorkbook(restaurantId: string, kind: 'template' | 'export', locale: 'en' | 'nl'): Promise<Blob> {
  const token = await getAccessToken();
  const response = await fetch(`/api/recipes/restaurant/${encodeURIComponent(restaurantId)}/allergen-import/${kind}?locale=${locale}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) await throwRecipeApiError(response);
  return response.blob();
}
